import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import RoleTitleAndDescription from './RoleTitleAndDescription';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import RolePermissions from './RolePermissions';
import { AreasData } from '../../types/areas';
import RolePageActionBar from './RolePageActionBar';
import { withExperiments, InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { setRole } from '../../redux/actions/serverApi.actions';
import { showModal } from '../../redux/actions/activeModal.actions';
import { ModalName } from '../../types/ModalNames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Role, Scope } from '../../types/roles';
import { validateRoleTitle } from '../../services/validators';
import Experiments from '@wix/wix-experiments';
import { useLocation } from 'react-router-dom';
import { removeStringDuplicates } from '../../services/useful';
import { Page, Container, Row, Col } from 'wix-style-react';
import { getIsScopePartOfARole } from '../../redux/selectors/roleAreas';

const getRoleTitle = (title: string, duplicateTitle: string, t): string => {
  let roleTitle;
  if (duplicateTitle) {
    roleTitle = t('role.copyOf', { roleTitle: duplicateTitle });
  } else if (title) {
    roleTitle = title;
  }
  return roleTitle;
};

const getAreasToDisplay = (areas: AreasData, selectedScopes: string[], experiments: Experiments, isScopePartOfARole: (scope: string) => boolean): AreasData => {
  return areas.flatMap(area => {
    const areaFilteredScopes = area.scopes.filter(scope => {
      if (!selectedScopes.includes(scope.id)) {
        if (scope.experiments && !scope.experiments.find(experiment => experiments.enabled(experiment))) {
          return false;
        } else if (scope.deprecated) {
          return experiments.enabled('specs.ident.ShouldShowSelectedDeprecatedScope') && isScopePartOfARole(scope.id);
        }
      }
      return true;
    });
    if (areaFilteredScopes.length > 0) {
      return [{ ...area, scopes: areaFilteredScopes }];
    } else {
      return [];
    }
  });
};

const calAlreadySelected = (scopeIds: string[], selectedScopes: string[]): boolean => {
  let alreadySelected = true;
  for (const scopeId of scopeIds) {
    if (!selectedScopes.includes(scopeId)) {
      alreadySelected = false;
      break;
    }
  }
  return alreadySelected;
};

const getSelectedScopes = (selectedScopes: string[], dependentScopes: string[], scopesMap?: { [key: string]: Scope }): string[] => {
  let newSelectedScopes = Array.from(new Set(selectedScopes.concat(dependentScopes)));
  if (scopesMap) {
    newSelectedScopes = newSelectedScopes.filter(scope => scopesMap[scope]);
  }
  return newSelectedScopes;
};

export const getDependentScopes = ((scopesMap: { [key: string]: Scope }, selectedScopes: string[]): string[] => {
  let dependenciesToIter = selectedScopes.reduce((dependencies, selectedScope) => {
    const dependentScopes = scopesMap[selectedScope]?.dependantScopes ?? [];
    return dependencies.concat(...dependentScopes);
  }, new Array<string>());
  let dependenciesToUpdate = new Set<string>();
  while (dependenciesToIter.length > 0) {
    const dependencyId = dependenciesToIter.pop();
    dependenciesToUpdate.add(dependencyId);
    const scope = scopesMap[dependencyId];
    if (scope && !selectedScopes.includes(dependencyId)) {
      const dependantScopes = scope.dependantScopes ?? [];
      dependenciesToIter.push(...dependantScopes);
    }

  }
  return Array.from(dependenciesToUpdate);
});

export const getScopesMap = (areasToDisplay): { [key: string]: Scope } => {
  let allScopes = {};
  areasToDisplay.forEach(area => area.scopes.forEach(scope => allScopes[scope.id] = scope));
  return allScopes;
};

interface IAppProps extends InjectedTranslateProps, InjectedExperimentsProps {
  id: string;
  title: string;
  description: string;
  isCustom: boolean;
  areas: AreasData;
  selectedScopes: string[];
  path: string;
  duplicateTitle?: string;
  edit?: boolean;
  roles?: Role[];
}

const RolePage: React.FC<IAppProps> = ({ t, experiments, edit, path, areas, isCustom, roles, ...role }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<any>();

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [dependentScopes, setDependentScopes] = useState<string[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [shouldUpdateScopes, setShouldUpdateScopes] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const titleRef = useRef(null);

  const isScopePartOfARole = useSelector(getIsScopePartOfARole);

  const areasToDisplay = useMemo(() => getAreasToDisplay(areas, role.selectedScopes, experiments, isScopePartOfARole), [areas, role.selectedScopes, experiments, isScopePartOfARole]);

  const scopesMap = useMemo<{ [key: string]: Scope }>(() => getScopesMap(areasToDisplay), [areasToDisplay]);

  const originPath = useMemo(() => location.state?.origin ?? '/roles' , [location]);

  const biOrigin = useMemo(() => location.state?.biOrigin || originPath, [location, originPath]);

  useEffect(() => {
    !title && setTitle(getRoleTitle(role.title, role.duplicateTitle, t));
    !description && setDescription(role.description || '');
    const newDependentScopes = getDependentScopes(scopesMap, role.selectedScopes);
    !selectedScopes.length && setSelectedScopes(role.selectedScopes.filter(selectedScope => !newDependentScopes.includes(selectedScope) && scopesMap[selectedScope]));
    setDependentScopes(newDependentScopes);
  }, [role.title, role.duplicateTitle, role.description, role.selectedScopes, scopesMap]);

  const readOnly = useMemo(() => !edit || !isCustom, [edit, isCustom]);

  const setRoleTitleError = useCallback((value) => {
    const validateRes = validateRoleTitle(value, roles, role.id);
    if (validateRes.isValid) {
      setErrorMessage('');
    } else {
      setErrorMessage(validateRes.errorMessage);
    }
    return validateRes.isValid;
  }, [roles, role.id]);

  const onTitleChange = useCallback(({ target: { value } }) => {
    setTitle(value);
    setDirty(true);
    setRoleTitleError(value);
  }, [roles]);

  const onDescriptionChange = useCallback(({ target: { value } }) => {
    setDescription(value);
    setDirty(true);
  }, []);

  const onScopeUpdate = useCallback((scopeId) => {
    const alreadySelected = selectedScopes.includes(scopeId);
    let newSelectedScopes;
    if (alreadySelected) {
      newSelectedScopes = selectedScopes.filter(selectedScope => selectedScope !== scopeId);
    } else {
      newSelectedScopes = selectedScopes.concat([scopeId]);
    }
    const newDependentScopes = getDependentScopes(scopesMap, newSelectedScopes);
    setDependentScopes(newDependentScopes);
    setSelectedScopes(newSelectedScopes.filter(selectedScope => !newDependentScopes.includes(selectedScope)));
    setDirty(true);
    setShouldUpdateScopes(true);
  }, [selectedScopes]);

  const onAreaSelectAllUpdate = useCallback((scopeIds) => {
    let alreadySelected = calAlreadySelected(scopeIds, selectedScopes.concat(dependentScopes));
    let newSelectedScopes;
    if (alreadySelected) {
      newSelectedScopes = selectedScopes.filter(selectedScope => !scopeIds.includes(selectedScope));
    } else {
      let scopesToSelect = [];
      scopeIds.forEach(scopeId => {
        if (!selectedScopes[scopeId]) {
          scopesToSelect.push(scopeId);
        }
      });
      newSelectedScopes = [...selectedScopes, ...scopesToSelect];
    }
    const newDependentScopes = getDependentScopes(scopesMap, newSelectedScopes);
    setDependentScopes(newDependentScopes);
    setSelectedScopes(newSelectedScopes.filter(newSelectedScope => !newDependentScopes.includes(newSelectedScope)));
    setDirty(true);
    setShouldUpdateScopes(true);
  }, [selectedScopes, dependentScopes, onScopeUpdate]);

  const getRedirectParams = useCallback((newRoleIds = []) => ({
    roleIds: removeStringDuplicates(newRoleIds, location.state?.roleIds),
    emails: location.state?.emails ?? [],
    origin: '/role',
  }), [location]);

  const saveRole = useCallback(async () => {
    const shouldSendCondition = experiments?.enabled('specs.ident.ShouldSendCustomRoleCondition');
    const roleId = await dispatch(setRole({ id: role.id, title, description, isCustom, scopes: getSelectedScopes(selectedScopes, dependentScopes) }, shouldUpdateScopes, biOrigin, !!role.duplicateTitle, shouldSendCondition));
    history.push(originPath, getRedirectParams([roleId]));
  }, [role.id, title, description, isCustom, selectedScopes, dependentScopes, shouldUpdateScopes, originPath, getRedirectParams, biOrigin, role.duplicateTitle, dispatch]);

  const onSaveAction = useCallback(() => {
    const isValid = setRoleTitleError(title);
    if (!isValid) {
      titleRef?.current?.input?.scrollIntoView(false);
      titleRef?.current?.input?.focus();
    } else {
      if (!dirty && role.duplicateTitle) {
        dispatch(showModal(ModalName.DuplicateRole, { duplicateTitle: role.duplicateTitle, onOk: saveRole }));
      } else if (selectedScopes.length === 0) {
        dispatch(showModal(ModalName.EmptyRole, { onOk: saveRole }));
      } else {
        saveRole();
      }
    }
  }, [dirty, role.duplicateTitle, selectedScopes, saveRole, experiments, dispatch]);

  const onBackAction = useCallback(() => history.push(originPath, getRedirectParams()), [originPath, history, getRedirectParams]);

  const onCancelAction = useCallback(() => {
    if (edit && dirty) {
      dispatch(showModal(ModalName.DiscardChanges, { onOk: onBackAction }));
    } else {
      onBackAction();
    }
  }, [edit, dirty, onBackAction, dispatch]);

  return (
    <Page height={'calc(100vh - 48px)'}>
      <Page.Header
        showBackButton
        onBackClicked={onBackAction}
        title={title || t('role.untitledRole')}
        breadcrumbs={<Breadcrumbs routePath={path} value={title || t('role.untitledRole')} disableChangePath={edit && dirty}/>}
        actionsBar={<RolePageActionBar id={role.id} edit={edit} onSaveAction={onSaveAction} onCancelAction={onCancelAction}/>}
      />
      <Page.Content>
        <Container>
          <Row stretchViewsVertically>
            <Col span={12}>
              <RoleTitleAndDescription
                titleRef={titleRef}
                title={title}
                description={description}
                readOnly={readOnly}
                onTitleChange={onTitleChange}
                onDescriptionChange={onDescriptionChange}
                errorMessage={errorMessage}
              />
            </Col>
          </Row>
          <Row stretchViewsVertically>
            <Col span={12}>
              <RolePermissions
                selectedScopes={getSelectedScopes(selectedScopes, dependentScopes, scopesMap)}
                disabledScopes={dependentScopes}
                areas={areasToDisplay}
                readOnly={readOnly}
                onScopeUpdate={onScopeUpdate}
                onAreaSelectAllUpdate={onAreaSelectAllUpdate}
              />
            </Col>
          </Row>
        </Container>
        <RolePageActionBar id={role.id} edit={edit} onSaveAction={onSaveAction} onCancelAction={onCancelAction} paddingTop="30px"/>
      </Page.Content>
    </Page>
  );
};

export default withExperiments(translate()(RolePage));
